import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  useAdminUser,
  useUpdateSubscriptionOverrides,
  useSetUserDisabled,
  useGiftScans,
  useGiftHistory,
  useDeleteUser,
} from "../../hooks/use-admin.js";
import { impersonateUser, downloadUserExport } from "../../api/admin.js";
import { PageSkeleton } from "../../components/skeleton.js";

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "\u2014";
  return new Date(iso).toLocaleDateString("en-IE");
}

export function AdminUserDetailPage() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { data, isLoading } = useAdminUser(userId);
  const updateOverrides = useUpdateSubscriptionOverrides(userId ?? "");
  const disableMutation = useSetUserDisabled(userId ?? "");

  const [overrideMaxWebsites, setOverrideMaxWebsites] = useState<string>("");
  const [overrideMaxPages, setOverrideMaxPages] = useState<string>("");
  const [overrideScansPerMonth, setOverrideScansPerMonth] =
    useState<string>("");
  const [overrideNotes, setOverrideNotes] = useState<string>("");
  const [overrideDocsEnabled, setOverrideDocsEnabled] = useState(false);
  const [overrideRecurringEnabled, setOverrideRecurringEnabled] =
    useState(false);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [giftAmount, setGiftAmount] = useState("");
  const [giftReason, setGiftReason] = useState("");
  const [giftWebsiteId, setGiftWebsiteId] = useState("");
  const [giftHistoryPage, setGiftHistoryPage] = useState(1);
  const [isExporting, setIsExporting] = useState(false);
  const deleteMutation = useDeleteUser();

  // Pre-populate override form from existing subscription values
  useEffect(() => {
    if (!data?.subscription) return;
    const sub = data.subscription;
    if (sub.overrideMaxWebsites != null) {
      setOverrideMaxWebsites(String(sub.overrideMaxWebsites));
    }
    if (sub.overrideMaxPagesPerScan != null) {
      setOverrideMaxPages(String(sub.overrideMaxPagesPerScan));
    }
    if (sub.overrideScansPerMonth != null) {
      setOverrideScansPerMonth(String(sub.overrideScansPerMonth));
    }
    if (sub.overrideNotes != null) {
      setOverrideNotes(sub.overrideNotes);
    }
    setOverrideDocsEnabled(sub.overrideDocsEnabled === 1);
    setOverrideRecurringEnabled(sub.overrideRecurringEnabled === 1);
  }, [data?.subscription]);

  async function handleImpersonate() {
    if (!userId) return;
    const reason = window.prompt(
      "Enter reason for impersonation (required for audit trail):",
    );
    if (!reason || reason.trim().length === 0) {
      return;
    }
    setIsImpersonating(true);
    try {
      const result = await impersonateUser(userId, reason.trim());
      window.location.href = result.redirectUrl;
    } catch {
      setIsImpersonating(false);
    }
  }

  async function handleSaveOverrides() {
    if (!userId) return;
    await updateOverrides.mutateAsync({
      overrideMaxWebsites: overrideMaxWebsites
        ? parseInt(overrideMaxWebsites, 10)
        : null,
      overrideMaxPagesPerScan: overrideMaxPages
        ? parseInt(overrideMaxPages, 10)
        : null,
      overrideScansPerMonth: overrideScansPerMonth
        ? parseInt(overrideScansPerMonth, 10)
        : null,
      overrideDocsEnabled: overrideDocsEnabled ? 1 : null,
      overrideRecurringEnabled: overrideRecurringEnabled ? 1 : null,
      overrideNotes: overrideNotes || null,
    });
  }

  const primaryOrgId = data?.orgs[0]?.id ?? "";
  const giftMutation = useGiftScans(primaryOrgId);
  const { data: giftHistoryData } = useGiftHistory(
    primaryOrgId || undefined,
    { page: giftHistoryPage, limit: 10 },
  );

  if (isLoading) return <PageSkeleton />;
  if (!data) return <div className="alert alert--error">User not found.</div>;

  const { user, orgs, subscription, websites } = data;

  return (
    <div className="product-page">
      <button
        type="button"
        className="btn btn--secondary btn--sm mb-3"
        onClick={() => navigate("/admin/users")}
      >
        &larr; Back to users
      </button>

      <h1 className="page-title">{user.email}</h1>

      <section className="product-section" aria-labelledby="user-heading">
        <h2 id="user-heading" className="section-title">
          User details
        </h2>
        <div className="stat-grid">
          <div className="stat-item">
            <span className="stat-item__value">{user.email}</span>
            <span className="stat-item__label">Email</span>
          </div>
          <div className="stat-item">
            <span className="stat-item__value">{fmtDate(user.createdAt)}</span>
            <span className="stat-item__label">Created</span>
          </div>
          <div className="stat-item">
            <span className="stat-item__value">
              {user.isAdmin ? "Yes" : "No"}
            </span>
            <span className="stat-item__label">Admin</span>
          </div>
          <div className="stat-item">
            <span className="stat-item__value">
              {user.disabledAt ? "Disabled" : "Active"}
            </span>
            <span className="stat-item__label">Status</span>
          </div>
        </div>
        <div className="btn-row mt-2">
          <button
            type="button"
            className="btn btn--secondary"
            onClick={() => void handleImpersonate()}
            disabled={isImpersonating || !!user.disabledAt}
          >
            {isImpersonating ? "Impersonating\u2026" : "Impersonate this user"}
          </button>
          <button
            type="button"
            className={user.disabledAt ? "btn btn--primary" : "btn btn--danger"}
            onClick={() => {
              const action = user.disabledAt ? "enable" : "disable";
              if (window.confirm(`Are you sure you want to ${action} ${user.email}?`)) {
                disableMutation.mutate(!user.disabledAt);
              }
            }}
            disabled={disableMutation.isPending}
          >
            {disableMutation.isPending
              ? "Saving\u2026"
              : user.disabledAt
                ? "Enable account"
                : "Disable account"}
          </button>
        </div>
      </section>

      {orgs.length > 0 && (
        <section className="product-section" aria-labelledby="orgs-heading">
          <h2 id="orgs-heading" className="section-title">
            Organisations ({orgs.length})
          </h2>
          <ul className="list-none">
            {orgs.map((org) => (
              <li key={org.id} className="py-2">
                <Link
                  to={`/admin/orgs/${org.id}`}
                  className="text-sm fw-500"
                >
                  {org.name}
                </Link>
                {" "}
                <span className="text-muted text-xs">
                  {"\u2014 "}
                  {org.role}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {subscription && (
        <section className="product-section" aria-labelledby="sub-heading">
          <h2 id="sub-heading" className="section-title">
            Subscription
          </h2>
          <div className="stat-grid">
            <div className="stat-item">
              <span className="stat-item__value">
                {subscription.plan?.name ?? subscription.planId}
              </span>
              <span className="stat-item__label">Plan</span>
            </div>
            <div className="stat-item">
              <span className="stat-item__value text-capitalize">
                {subscription.status}
              </span>
              <span className="stat-item__label">Status</span>
            </div>
            {subscription.trialEndsAt && (
              <div className="stat-item">
                <span className="stat-item__value">
                  {fmtDate(subscription.trialEndsAt)}
                </span>
                <span className="stat-item__label">Trial ends</span>
              </div>
            )}
            {subscription.currentPeriodStart && (
              <div className="stat-item">
                <span className="stat-item__value">
                  {fmtDate(subscription.currentPeriodStart)}
                </span>
                <span className="stat-item__label">Period start</span>
              </div>
            )}
            {subscription.currentPeriodEnd && (
              <div className="stat-item">
                <span className="stat-item__value">
                  {fmtDate(subscription.currentPeriodEnd)}
                </span>
                <span className="stat-item__label">Period end</span>
              </div>
            )}
          </div>

          <h3 className="fw-600 text-sm mt-3">Override limits</h3>
          <p className="text-muted text-sm">
            Leave blank to use plan defaults.
          </p>
          <div className="admin-form-stack mt-1">
            <label>
              <span className="text-sm">Max websites</span>
              <input
                type="number"
                className="form-input"
                value={overrideMaxWebsites}
                onChange={(e) => setOverrideMaxWebsites(e.target.value)}
                placeholder={String(subscription.plan?.maxWebsites ?? "")}
              />
            </label>
            <label>
              <span className="text-sm">Max pages per scan</span>
              <input
                type="number"
                className="form-input"
                value={overrideMaxPages}
                onChange={(e) => setOverrideMaxPages(e.target.value)}
                placeholder={String(subscription.plan?.maxPagesPerScan ?? "")}
              />
            </label>
            <label>
              <span className="text-sm">
                Scans per month per website{" "}
                <span className="text-muted">(set high for testing)</span>
              </span>
              <input
                type="number"
                className="form-input"
                value={overrideScansPerMonth}
                onChange={(e) => setOverrideScansPerMonth(e.target.value)}
                placeholder={String(
                  subscription.plan?.scansPerMonthPerWebsite ?? "",
                )}
              />
            </label>
            <div className="form-checkbox-row">
              <input
                type="checkbox"
                id="override-docs"
                checked={overrideDocsEnabled}
                onChange={(e) => setOverrideDocsEnabled(e.target.checked)}
              />
              <label htmlFor="override-docs" className="text-sm">
                Override: enable document scanning
              </label>
            </div>
            <div className="form-checkbox-row">
              <input
                type="checkbox"
                id="override-recurring"
                checked={overrideRecurringEnabled}
                onChange={(e) => setOverrideRecurringEnabled(e.target.checked)}
              />
              <label htmlFor="override-recurring" className="text-sm">
                Override: enable recurring monitoring
              </label>
            </div>
            <label>
              <span className="text-sm">Notes</span>
              <input
                type="text"
                className="form-input"
                value={overrideNotes}
                onChange={(e) => setOverrideNotes(e.target.value)}
                placeholder="e.g. early customer discount"
              />
            </label>
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => void handleSaveOverrides()}
              disabled={updateOverrides.isPending}
            >
              {updateOverrides.isPending ? "Saving\u2026" : "Save overrides"}
            </button>
            {updateOverrides.isSuccess && (
              <p className="text-muted text-sm">Saved successfully.</p>
            )}
          </div>
        </section>
      )}

      {primaryOrgId && (
        <section className="product-section" aria-labelledby="gift-heading">
          <h2 id="gift-heading" className="section-title">
            Gift scans
          </h2>
          <p className="text-muted text-sm">
            Gift extra scans as compensation. Max 50 per action, 200 per org
            per month. Expires after 90 days.
          </p>
          <div className="admin-form-stack mt-1">
            <label>
              <span className="text-sm">Number of scans (1–50)</span>
              <input
                type="number"
                className="form-input"
                min="1"
                max="50"
                value={giftAmount}
                onChange={(e) => setGiftAmount(e.target.value)}
              />
            </label>
            <label>
              <span className="text-sm">Reason (required)</span>
              <input
                type="text"
                className="form-input"
                placeholder="e.g. Compensation for scan failure on 2026-04-01"
                value={giftReason}
                onChange={(e) => setGiftReason(e.target.value)}
              />
            </label>
            {websites.length > 0 && (
              <label>
                <span className="text-sm">
                  Website (optional, leave blank for all)
                </span>
                <select
                  className="form-input"
                  value={giftWebsiteId}
                  onChange={(e) => setGiftWebsiteId(e.target.value)}
                >
                  <option value="">All websites</option>
                  {websites.map((w) => (
                    <option key={w.id} value={w.id}>
                      {w.url}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <button
              type="button"
              className="btn btn--primary"
              disabled={
                giftMutation.isPending ||
                !giftAmount ||
                !giftReason.trim()
              }
              onClick={async () => {
                const amt = parseInt(giftAmount, 10);
                if (!amt || amt < 1 || amt > 50 || !giftReason.trim()) return;
                try {
                  await giftMutation.mutateAsync({
                    amount: amt,
                    reason: giftReason.trim(),
                    websiteId: giftWebsiteId || null,
                  });
                  setGiftAmount("");
                  setGiftReason("");
                  setGiftWebsiteId("");
                } catch {
                  // error shown via giftMutation.isError
                }
              }}
            >
              {giftMutation.isPending ? "Gifting\u2026" : "Gift scans"}
            </button>
            {giftMutation.isSuccess && (
              <p className="text-muted text-sm">Scans gifted successfully.</p>
            )}
            {giftMutation.isError && (
              <p className="alert alert--error text-sm">
                {giftMutation.error instanceof Error
                  ? giftMutation.error.message
                  : "Failed to gift scans"}
              </p>
            )}
          </div>

          {giftHistoryData && giftHistoryData.credits.length > 0 && (
            <>
              <h3 className="fw-600 text-sm mt-3">Gift history</h3>
              <div className="table-responsive mt-1">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Amount</th>
                      <th>Remaining</th>
                      <th>Reason</th>
                      <th>Admin</th>
                      <th>Expires</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {giftHistoryData.credits.map((c) => (
                      <tr key={c.id}>
                        <td className="text-nowrap">{fmtDate(c.createdAt)}</td>
                        <td>{c.amount}</td>
                        <td>{c.remaining}</td>
                        <td>{c.reason}</td>
                        <td className="text-nowrap">{c.giftedByEmail}</td>
                        <td className="text-nowrap">{fmtDate(c.expiresAt)}</td>
                        <td>
                          <span
                            className={`badge ${
                              c.isFullyUsed
                                ? "badge--muted"
                                : c.isExpired
                                  ? "badge--danger"
                                  : "badge--success"
                            }`}
                          >
                            {c.isFullyUsed
                              ? "Used"
                              : c.isExpired
                                ? "Expired"
                                : "Active"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {giftHistoryData.total > 10 && (
                <div className="d-flex gap-2 items-center mt-2">
                  <button
                    type="button"
                    className="btn btn--secondary btn--sm"
                    onClick={() =>
                      setGiftHistoryPage((p) => Math.max(1, p - 1))
                    }
                    disabled={giftHistoryPage === 1}
                  >
                    Previous
                  </button>
                  <span className="text-muted text-sm">
                    Page {giftHistoryPage} /{" "}
                    {Math.ceil(giftHistoryData.total / 10)}
                  </span>
                  <button
                    type="button"
                    className="btn btn--secondary btn--sm"
                    onClick={() => setGiftHistoryPage((p) => p + 1)}
                    disabled={giftHistoryData.credits.length < 10}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </section>
      )}

      {websites.length > 0 && (
        <section className="product-section" aria-labelledby="sites-heading">
          <h2 id="sites-heading" className="section-title">
            Websites ({websites.length})
          </h2>
          <ul className="list-none">
            {websites.map((site) => (
              <li key={site.id} className="py-2">
                <Link
                  to={`/admin/websites/${site.id}`}
                  className="text-sm"
                >
                  {site.url}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="product-section" aria-labelledby="gdpr-heading">
        <h2 id="gdpr-heading" className="section-title">
          GDPR / Data management
        </h2>
        <p className="text-muted text-sm">
          Export or permanently delete this user&apos;s account and all associated data.
        </p>
        <div className="btn-row mt-2">
          <button
            type="button"
            className="btn btn--secondary"
            disabled={isExporting}
            onClick={async () => {
              if (!userId) return;
              setIsExporting(true);
              try {
                await downloadUserExport(userId);
              } catch (err) {
                alert(err instanceof Error ? err.message : "Export failed");
              } finally {
                setIsExporting(false);
              }
            }}
          >
            {isExporting ? "Exporting\u2026" : "Export user data (JSON)"}
          </button>
          <button
            type="button"
            className="btn btn--danger"
            disabled={deleteMutation.isPending || !!user.isAdmin}
            onClick={() => {
              if (!userId) return;
              const confirmed = window.confirm(
                `PERMANENT ACTION: Delete ${user.email} and all their data?\n\nThis cannot be undone. The user's org memberships, tokens, and pipeline executions will be removed.`,
              );
              if (confirmed) {
                deleteMutation.mutate(userId, {
                  onSuccess: () => navigate("/admin/users"),
                });
              }
            }}
          >
            {deleteMutation.isPending ? "Deleting\u2026" : "Delete account permanently"}
          </button>
        </div>
        {deleteMutation.isError && (
          <p className="alert alert--error text-sm mt-2">
            {deleteMutation.error instanceof Error
              ? deleteMutation.error.message
              : "Failed to delete account"}
          </p>
        )}
      </section>
    </div>
  );
}
